import { isNil } from 'lodash';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { imageSrcToBlob } from '../images/image-utils';
import { ChapterModel } from '../story/models/chapter.model';
import { ContentType } from '../story/models/content-type.enum';
import { StoryChapterThumbnail } from '../story/models/story-chapter-thumbnail.model';
import { storyType } from '../story/state/story-type';

// thumbnail urls
export namespace Thumbnail {
  export function generateLegacyThumbnail(storyId: string, chapterId: string) {
    const isBookmark = ContentType.Bookmark === storyType(storyId);
    const pathForStoryType = isBookmark ? 'bookmarks' : 'modules';
    const urlBase = `/thumbs/${pathForStoryType}/${storyId.replace('.json', '')}`;

    return {
      small: `${urlBase}/small/chapters/${chapterId}.jpg`,
      large: `${urlBase}/large/chapters/${chapterId}.jpg`,
    };
  }

  export function generateThumbnails(
    storyIdChapterIdList: {
      originalChapterId: string;
      newChapterId: string;
      originalStoryId: string;
    }[],
  ): Map<string, StoryChapterThumbnail> {
    return storyIdChapterIdList.reduce(
      (acc, { originalChapterId, newChapterId, originalStoryId }) => {
        const isBookmark = ContentType.Bookmark === storyType(originalStoryId);

        const thumbnail = new StoryChapterThumbnail();
        thumbnail.storyId = originalStoryId;
        thumbnail.chapterId = originalChapterId;

        const pathForStoryType = isBookmark ? 'bookmarks' : 'modules';
        const urlBase = `/thumbs/${pathForStoryType}/${originalStoryId.replace('.json', '')}`;

        thumbnail.urls = {
          small: `${urlBase}/small/chapters/${originalChapterId}.jpg`,
          large: `${urlBase}/large/chapters/${originalChapterId}.jpg`,
        };
        acc.set(newChapterId, thumbnail);
        return acc;
      },
      new Map(),
    );
  }

  export function getChapterThumbnail(chapter: ChapterModel, storyId: string) {
    const thumbnail = new StoryChapterThumbnail();

    if (chapter.thumbnails) {
      thumbnail.legacy = false;
      thumbnail.storyId = storyId;
      thumbnail.chapterId = chapter.id;
      thumbnail.urls = {
        ...chapter.thumbnails,
      };
    } else {
      const thumbnailPath = ContentType.Bookmark === storyType(storyId) ? 'bookmarks' : 'modules';
      const urlBase = `/thumbs/${thumbnailPath}/${storyId.replace('.json', '')}`;

      thumbnail.legacy = true;
      thumbnail.storyId = storyId;
      thumbnail.chapterId = chapter.id;

      thumbnail.urls = {
        small: `${urlBase}/small/chapters/${chapter.id}.jpg`,
        large: `${urlBase}/large/chapters/${chapter.id}.jpg`,
      };
    }

    return thumbnail;
  }

  export function getChapterThumbnails(
    storyId: string,
    chapters: ChapterModel[],
  ): Map<string, StoryChapterThumbnail> {
    return chapters.reduce((result, chapter) => {
      const thumbnail = getChapterThumbnail(chapter, storyId);
      result.set(chapter.id, thumbnail);
      return result;
    }, new Map());
  }

  // Load the image, then record the blob so it saves as new
  export function makeFreshThumbnail(storyId: string, chapterId: string, url: string) {
    return imageSrcToBlob(url).pipe(
      filter((blob) => !isNil(blob)),
      map((blob) => {
        const thumbnail = new StoryChapterThumbnail();
        thumbnail.snapshot = URL.createObjectURL(blob);
        thumbnail.chapterId = chapterId;
        thumbnail.storyId = storyId;

        return thumbnail;
      }),
    );
  }
}
